import type {
  AxisCalibration,
  AxisQuantity,
  ChartCalibration,
  ChartRegion,
  CurveDraftDocument,
  CurveImportRead,
  CurveKind,
  CurveTraceDraft,
  TracePointDraft,
} from './types';

export const EMPTY_CURVE_DOCUMENT: CurveDraftDocument = {
  calibration: null,
  selected_chart: null,
  selected_page: null,
  traces: [],
};

function identifier(): string {
  return globalThis.crypto.randomUUID();
}

export function documentFromImport(imported: CurveImportRead): CurveDraftDocument {
  return {
    calibration: imported.calibration,
    selected_chart: imported.selected_chart,
    selected_page: imported.selected_page,
    traces: imported.traces,
  };
}

function axis(
  quantity: AxisQuantity,
  unit: string,
  pixelStart: number,
  pixelEnd: number,
  valueEnd: number,
): AxisCalibration {
  return {
    direction: 'increasing',
    pixel_end: pixelEnd,
    pixel_start: pixelStart,
    quantity,
    scale: 'linear',
    unit,
    value_end: valueEnd,
    value_start: 0,
  };
}

export function defaultChartRegion(
  pageNumber: number,
  width: number,
  height: number,
): ChartRegion {
  return {
    bottom_px: height,
    label: 'Primary chart',
    left_px: 0,
    page_number: pageNumber,
    right_px: width,
    top_px: 0,
  };
}

export function defaultCalibration(region: ChartRegion): ChartCalibration {
  return {
    x_axis: axis('flow', 'gpm', region.left_px, region.right_px, 1000),
    y_axis: axis('head', 'ft', region.bottom_px, region.top_px, 100),
  };
}

function contourDefaults(kind: CurveKind): Pick<
  CurveTraceDraft,
  'contour_unit' | 'contour_value'
> {
  if (kind === 'efficiency_contour') {
    return { contour_unit: '%', contour_value: 75 };
  }
  if (kind === 'brake_power_contour') {
    return { contour_unit: 'hp', contour_value: 1 };
  }
  if (kind === 'npshr_contour') {
    return { contour_unit: 'ft', contour_value: 1 };
  }
  return { contour_unit: null, contour_value: null };
}

export function newTrace(
  kind: CurveKind,
  label: string,
  pageNumber: number,
  region: ChartRegion,
  speedRpm: number | null,
): CurveTraceDraft {
  const width = region.right_px - region.left_px;
  const height = region.bottom_px - region.top_px;
  const points: TracePointDraft[] = [0.25, 0.75].map((fraction, sequence) => ({
    id: identifier(),
    pixel: {
      x: region.left_px + width * fraction,
      y: region.top_px + height * fraction,
    },
    sequence,
    source: null,
  }));
  return {
    calibration_override: null,
    chart_region: region,
    ...contourDefaults(kind),
    id: identifier(),
    kind,
    label,
    origin: 'measured',
    page_number: pageNumber,
    points,
    speed_rpm: speedRpm,
    suggested_by: null,
    suggestion_confidence: null,
    trim_label: null,
  };
}

export function replaceTrace(
  document: CurveDraftDocument,
  traceId: string,
  replacement: CurveTraceDraft,
): CurveDraftDocument {
  return {
    ...document,
    traces: document.traces.map((trace) =>
      trace.id === traceId ? replacement : trace,
    ),
  };
}

export function updateTrace(
  document: CurveDraftDocument,
  traceId: string,
  update: (trace: CurveTraceDraft) => CurveTraceDraft,
): CurveDraftDocument {
  return {
    ...document,
    traces: document.traces.map((trace) =>
      trace.id === traceId ? update(trace) : trace,
    ),
  };
}

export function nextPointSequence(trace: CurveTraceDraft): number {
  return Math.max(-1, ...trace.points.map((point) => point.sequence)) + 1;
}

export function newPointId(): string {
  return identifier();
}
